/**
 * Session expiry metadata for the browser client.
 *
 * HttpOnly refresh-token cookies cannot be read from JavaScript, so cookie
 * Max-Age is not available here. The server must signal expiry via API fields
 * and/or a refreshToken JWT in the response body (whose exp should match the
 * cookie lifetime when the backend is configured correctly).
 *
 * Two deadlines are tracked:
 * - rolling: idle / sliding expiry; updated on login and every refresh-session
 * - absolute: hard session ceiling from the server; set at login, never extended
 *
 * Warning and client-side refresh guards use whichever deadline is sooner.
 */

const STORAGE_KEYS = {
  rolling: "sessionRollingExpMs",
  absolute: "sessionAbsoluteExpMs",
  legacy: "refreshTokenExpMs",
};

const ABSOLUTE_EXPIRY_FIELDS = [
  "sessionExpiresAt",
  "absoluteExpiresAt",
  "absoluteSessionExpiresAt",
  "session_expires_at",
];

const ROLLING_EXPIRY_FIELDS = [
  "refreshExpiresAt",
  "refresh_expires_at",
  "expiresAt",
];

function readStorage(key) {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStorage(key, ms) {
  try {
    localStorage.setItem(key, String(ms));
  } catch {
    /* ignore */
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** ISO-8601 string, unix seconds, or unix milliseconds → ms since epoch */
export function parseExpiresAtMs(value) {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const asNum = Number(trimmed);
    if (Number.isFinite(asNum)) {
      return asNum < 1e12 ? asNum * 1000 : asNum;
    }
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readJwtExpMs(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  try {
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function firstParsedMs(data, fields) {
  for (const field of fields) {
    const ms = parseExpiresAtMs(data?.[field]);
    if (ms != null) return ms;
  }
  return null;
}

function resolveRollingExpMs(data) {
  const fromField = firstParsedMs(data, ROLLING_EXPIRY_FIELDS);
  if (fromField != null) return fromField;
  if (data?.refreshToken) return readJwtExpMs(data.refreshToken);
  return null;
}

function resolveAbsoluteExpMs(data) {
  return firstParsedMs(data, ABSOLUTE_EXPIRY_FIELDS);
}

/**
 * @param {object} data - auth payload (login, verify-login, refresh-session, …)
 * @param {{ isRefresh?: boolean }} options
 */
export function syncSessionExpiryFromPayload(data, { isRefresh = false } = {}) {
  if (!data) return;

  const rollingMs = resolveRollingExpMs(data);
  const absoluteMs = resolveAbsoluteExpMs(data);

  if (rollingMs != null) {
    writeStorage(STORAGE_KEYS.rolling, rollingMs);
    writeStorage(STORAGE_KEYS.legacy, rollingMs);
  }

  if (absoluteMs != null) {
    if (isRefresh) {
      const existing = getAbsoluteSessionExpMs();
      writeStorage(
        STORAGE_KEYS.absolute,
        existing != null ? Math.min(existing, absoluteMs) : absoluteMs
      );
    } else {
      writeStorage(STORAGE_KEYS.absolute, absoluteMs);
    }
  }
}

/** @deprecated Use syncSessionExpiryFromPayload */
export function syncRefreshExpiryFromAuthPayload(data) {
  syncSessionExpiryFromPayload(data, { isRefresh: false });
}

/** @deprecated Use syncSessionExpiryFromPayload */
export function setRefreshExpiryFromJwt(refreshToken) {
  syncSessionExpiryFromPayload({ refreshToken }, { isRefresh: true });
}

export function getRollingSessionExpMs() {
  return (
    readStorage(STORAGE_KEYS.rolling) ?? readStorage(STORAGE_KEYS.legacy)
  );
}

export function getAbsoluteSessionExpMs() {
  return readStorage(STORAGE_KEYS.absolute);
}

/** Soonest time the session is expected to end (ms since epoch). */
export function getLogoutDeadlineMs() {
  const candidates = [getRollingSessionExpMs(), getAbsoluteSessionExpMs()].filter(
    (ms) => ms != null
  );
  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

export function getMsUntilLogout() {
  const deadline = getLogoutDeadlineMs();
  if (deadline == null) return null;
  return deadline - Date.now();
}

/** Used by refresh guard in api.js */
export function getRefreshExpMs() {
  return getRollingSessionExpMs();
}

export function clearRefreshExpiryMeta() {
  for (const key of Object.values(STORAGE_KEYS)) {
    removeStorage(key);
  }
}
